import { useCallback, useEffect, useState } from "react";
import { LifeBuoy, RefreshCw, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import AdminTabsNav from "@/components/AdminTabsNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWallet } from "@/context/WalletContext";
import { API, AttachmentPicker, MessageThread, StatusBadge, fmtDate, shortAddr, statusLabel } from "@/components/SupportShared";

const STATUSES = ["open", "pending", "review", "response_sent", "completed", "closed"];

const StatusSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-44" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`${testid}-${s}`}>{statusLabel(s)}</SelectItem>)}</SelectContent>
  </Select>
);

const AdminTicket = ({ ticket, headers, addr, onBack, onChanged }) => {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [replyStatus, setReplyStatus] = useState("response_sent");
  const jsonHeaders = { ...headers, "Content-Type": "application/json" };

  const reply = async () => {
    const r = await fetch(`${API}/api/admin/support/tickets/${ticket.number}/reply`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ message, attachments: files.map((f) => f.id), status: replyStatus }),
    });
    if (!r.ok) return toast.error("Reply failed");
    toast.success("Response sent");
    setMessage(""); setFiles([]);
    onChanged(await r.json());
  };

  const setStatus = async (status) => {
    const r = await fetch(`${API}/api/admin/support/tickets/${ticket.number}`, { method: "PATCH", headers: jsonHeaders, body: JSON.stringify({ status }) });
    if (r.ok) { toast.success(`Status: ${statusLabel(status)}`); onChanged({ ...ticket, status }); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ticket #${ticket.number}? This cannot be undone.`)) return;
    const r = await fetch(`${API}/api/admin/support/tickets/${ticket.number}`, { method: "DELETE", headers });
    if (r.ok) { toast.success("Ticket deleted"); onBack(); }
  };

  return (
    <div className="space-y-5" data-testid="admin-support-detail">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="admin-support-back">
        <ArrowLeft className="h-4 w-4" />All tickets
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-black" data-testid="admin-support-number">#{ticket.number} · {ticket.subject}</h2>
          <p className="mt-1 font-mono text-xs text-muted-foreground break-all" data-testid="admin-support-wallet">{ticket.user_address}</p>
          <p className="text-xs text-muted-foreground">Opened {fmtDate(ticket.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusSelect value={ticket.status} onChange={setStatus} testid="admin-support-status-select" />
          <Button variant="destructive" size="icon" onClick={remove} data-testid="admin-support-delete"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <MessageThread messages={ticket.messages} viewerAddr={addr} />
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Write a response…" data-testid="admin-support-reply-input" />
        <AttachmentPicker headers={headers} files={files} onChange={setFiles} testid="admin-support-attach" />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Set status to</span>
          <StatusSelect value={replyStatus} onChange={setReplyStatus} testid="admin-support-reply-status" />
          <Button onClick={reply} disabled={!message.trim() && !files.length} className="rounded-full" data-testid="admin-support-reply-button">Send response</Button>
        </div>
      </div>
    </div>
  );
};

export default function AdminSupport() {
  const { getAuthHeaders, addresses } = useWallet();
  const [data, setData] = useState({ tickets: [], counts: {} });
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [denied, setDenied] = useState(false);
  const headers = getAuthHeaders();
  const addr = addresses?.stx;

  const load = useCallback(async () => {
    if (!addr) return;
    const r = await fetch(`${API}/api/admin/support/tickets${filter ? `?status=${filter}` : ""}`, { headers: { "X-STX-Address": addr } });
    if (r.status === 403) return setDenied(true);
    if (r.ok) { setDenied(false); setData(await r.json()); }
  }, [addr, filter]);

  useEffect(() => { load(); }, [load]);

  const open = async (n) => {
    const r = await fetch(`${API}/api/admin/support/tickets/${n}`, { headers });
    if (r.ok) setActive(await r.json());
  };

  const total = Object.values(data.counts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="admin-support-page">
        <AdminTabsNav active="support" />
        <div className="mb-5 flex items-center justify-between">
          <h1 className="flex items-center gap-2 text-2xl font-bold"><LifeBuoy className="h-6 w-6 text-orange-400" />Support Tickets</h1>
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-support-refresh"><RefreshCw className="h-4 w-4" />Refresh</Button>
        </div>
        {!addr ? <p className="text-muted-foreground">Connect an admin wallet.</p> : denied ? (
          <p className="text-red-400" data-testid="admin-support-denied">Admin access required.</p>
        ) : active ? (
          <AdminTicket ticket={active} headers={headers} addr={addr} onBack={() => { setActive(null); load(); }} onChanged={(t) => { setActive(t); load(); }} />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-support-filters">
              {[["", `All (${total})`], ...STATUSES.map((s) => [s, `${statusLabel(s)} (${data.counts[s] || 0})`])].map(([k, label]) => (
                <button key={k || "all"} type="button" onClick={() => setFilter(k)} data-testid={`admin-support-filter-${k || "all"}`}
                  className={`rounded-full border px-3 py-1 text-xs ${filter === k ? "border-orange-500 bg-orange-500 text-black" : "border-white/15 text-muted-foreground hover:border-orange-500/60"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {data.tickets.length === 0 && <p className="text-sm text-muted-foreground" data-testid="admin-support-empty">No tickets.</p>}
              {data.tickets.map((t) => (
                <button key={t.number} type="button" onClick={() => open(t.number)} data-testid={`admin-support-row-${t.number}`}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-left hover:border-orange-500/50 transition-colors">
                  <div className="min-w-0">
                    <p className="font-semibold">#{t.number} · {t.subject}</p>
                    <p className="truncate text-xs text-muted-foreground">{shortAddr(t.user_address)} · {fmtDate(t.updated_at)} · {t.messages.length} msgs · {t.messages[t.messages.length - 1]?.message}</p>
                  </div>
                  <StatusBadge status={t.status} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
